import React from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, DesktopTimePicker, DateValidationError } from "@mui/x-date-pickers";
import { Box } from "@mui/material";
import { ReactComponent as CalendarIcon } from "../../../assets/images/calendar.svg";
import { ReactComponent as ClockIcon } from "../../../assets/images/clock.svg";

type CommonDateTimePickerProps = {
  type: "date" | "time";
  value: Date | undefined;
  onChange?: (date: Date | null) => void;
  onValueChange?: (date: Date | null) => void;
  readOnly?: boolean;
  errorExist?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  dataTestId?: string;
  // Date specific
  maxDate?: Date;
  futureDate?: boolean;
  startDate?: Date;
  disabled?: boolean;
  className?: string;
  styleChange?: boolean;
  borderRight?: boolean;
  // Time specific
  diffStyle?: boolean;
};

const CommonDateTimePicker: React.FC<CommonDateTimePickerProps> = ({
  type,
  value,
  onChange,
  onValueChange,
  readOnly = false,
  errorExist,
  onKeyDown,
  dataTestId,
  // Date specific
  maxDate,
  futureDate,
  startDate,
  disabled,
  className,
  styleChange,
  borderRight,
  // Time specific
  diffStyle,
}) => {
  const handleDateChange = (date: Date | null) => {
    if (date) {
      const isValidDate = !isNaN(date.getTime());
      if (isValidDate) {
        const formattedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1)
          .toISOString()
          .split("T")[0];
        onChange && onChange(new Date(formattedDate));
        if (onValueChange) onValueChange(new Date(formattedDate));
      } else {
        onChange && onChange(date);
        if (onValueChange) onValueChange(date);
      }
    } else {
      onChange && onChange(null);
      if (onValueChange) onValueChange(null);
    }
  };

  const handleError = (error: DateValidationError, value: Date | null) => {
    console.error("Date picker error:", error, value);
  };

  return (
    // Generated by Copilot
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {type === "date" ? (
        <Box    
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            position: "relative",
          }}
          data-testid="date-picker-box"
        >
          <DatePicker
            localeText={{
              fieldMonthPlaceholder: () => "MMM",
            }}
            value={value}
            onChange={handleDateChange}
            onError={handleError}
            minDate={startDate ? startDate : undefined}
            maxDate={futureDate ? maxDate || undefined : undefined}
            format="dd-MMM-yyyy"
            slots={{
              openPickerIcon: (props) => (
                <CalendarIcon {...props} data-testid="calendar-icon" />
              ),
            }}
            sx={{
              width: "100%",
              fieldset: {
                border: "1px solid #D9D9D9 !important",
                borderColor: errorExist ? "#E28619 !important" : "#C9C9C9 !important",
                borderRadius: styleChange ? "0.375rem" : "0.375rem 0px 0px 0.375rem",
                borderRight:
                  borderRight && errorExist ? "1px solid #C9C9C9 !important" : "",
                color: "#051B46",
                fontSize: 26,
              },
              "&:hover": {
                fieldset: {
                  // borderColor: readOnly ? "#8A8A8A" : "#1859B4 !important",
                },
                // "& .MuiOutlinedInput": {
                //   height: 30
                // },
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: readOnly ? "#8A8A8A" : "#1859B4",
                  borderRight:
                    borderRight && errorExist
                      ? "1px solid #C9C9C9 !important"
                      : "",
                },
              },
              "&:focus-within": {
                fieldset: {
                  borderColor: readOnly ? "#8A8A8A" : "#1859B4 !important",
                },
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: readOnly ? "#8A8A8A" : "#1859B4 !important",
                },
              },
              "& input::placeholder": {
                fontSize: 14,
              },
              "& .MuiInputBase-input": {
                color: "#051b46",
                fontSize: 14,
                textAlign: "left",
                fontWeight: 400,
                padding: "8px",
                borderRight: "1px solid #C9C9C9",
                cursor: readOnly ? "not-allowed" : "default",
                height: 30
              },
              input: {
                fontSize: 14,
                textAlign: "left",
                fontWeight: 400,
              },
              "& .MuiInputBase-root": {
                backgroundColor: readOnly ? "#f6f6f6" : "#fff",
                height: 48,
                flexDirection: "row",
                width: "100%",
                cursor: readOnly ? "not-allowed" : "default",
              },
              "& .MuiInputAdornment-root ": {
                marginLeft: "0px !important",
              },
            }}
            readOnly={readOnly}
            disabled={disabled}
            className={className}
            data-testid="date-picker-input"
          />
        </Box>
      ) : (
        <DesktopTimePicker
          value={value}
          onChange={onChange}
          ampm={true}
          format={"hh:mm aaa"}
          slots={{
            openPickerIcon: (props) => (
              <ClockIcon {...props} data-testid="clock-icon" />
            ),
          }}
          sx={{
            fieldset: {
              borderColor: errorExist ? "#E28619" : "#C9C9C9 !important",
              borderTopWidth: 1,
              borderRightWidth: 1,
              borderBottomWidth: 1,
              borderLeftWidth: 1,
              borderRadius: diffStyle ? "0.375rem" : "0px 0.375rem 0.375rem 0px",
              borderLeft: diffStyle ? "" : "none",
            },
            "&:hover": {
              fieldset: {
                // borderColor: readOnly ? "#8A8A8A" : "#1859B4 !important",
              },
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: readOnly ? "#8A8A8A" : "#1859B4",
              },
            },
            "&:focus-within": {
              fieldset: { borderColor: "#8A8A8A" },
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: readOnly ? "#8A8A8A" : "#1859B4",
              },
            },
            "& input::placeholder": {
              textColor: "#051b46",
              fontSize: 14,
            },
            "& .MuiInputBase-input": {
              color: "#051b46",
              fontSize: 14,
              textAlign: "left",
              fontWeight: 400,
              padding: "8px",
              height: 30,
              borderRight: "1px solid #C9C9C9",
            },
            "& .MuiInputBase-root": {
              backgroundColor: readOnly ? "#f6f6f6" : "#fff",
              height: 48,
              width: '100%',
              flexDirection: { flexDirection: "row" },
            },
            "& .MuiInputAdornment-root": {
              marginLeft: "0px",
            },
          }}
          slotProps={{
            textField: {
              size: "medium",
              fullWidth: true,
              required: false,
              autoFocus: false,
              error: false,
              onKeyDown,
            },
            openPickerButton: {
              "data-testid": `${dataTestId}-timer-icon`,
            },
            layout: {
              sx: {
                ul: {
                  "::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "::-webkit-scrollbar-track": {
                    background: "unset !important",
                  },
                  "::-webkit-scrollbar-thumb": {
                    background: "#D9D9D9",
                    borderRadius: "10px",
                  },
                  "::-webkit-scrollbar-thumb:hover": {
                    background: "#555",
                  },
                },
              },
            },
          }}
          readOnly={readOnly}
          data-testid="desktop-time-picker"
        />
      )}
    </LocalizationProvider>
  );
};

export default CommonDateTimePicker;